import { rem } from "polished";
import styled from "styled-components";

import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import type { ImageFit } from "@/components/editor/shared/header/header-settings-component";
import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import StyledButtonContainer from "@/components/editor/themes/nolan/shared/cta-button";
import { ColorLabels } from "@/components/editor/utils/use-color";

export const PaddingWrapper = withViewportStyle("div")`
  padding: ${rem(24)};

  ${(props) => props.$viewport.mobile`
    padding: ${rem(16)};
  `}
`;

export const Container = withViewportStyle("div")`
  padding: ${rem(64)} ${rem(84)} ${rem(58)};

  ${(props) => props.$viewport.tablet`
    padding: ${rem(40)} 0 0;
  `}
`;

export const ContentWrapper = withViewportStyle("div")<{
  isLogoShowing: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${rem(48)};
  
  .text-primitive {
    text-align: left;
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  }

  .ck-content h1,
  .ck-content h2,
  .ck-content h3,
  .ck-content h4,
  .ck-content h5,
  .ck-content p {
    margin: 0;
  }

  ${(props) => props.$viewport.tablet`
    grid-template-columns: 1fr;
    grid-gap: ${rem(24)};
    grid-template-areas: ${(props) =>
      props.isLogoShowing &&
      '"logo"'} "heading" "ctaText" "subheading" "button";

    .text-primitive,
    .ck-content h1,
    .ck-content h2,
    .ck-content h3,
    .ck-content h4,
    .ck-content h5,
    .ck-content p {
      text-align: center;
    }
  `}

  ${(props) => props.$viewport.mobile`
    grid-gap: ${rem(24)};
  `}
`;

export const LeftColumn = withViewportStyle("div")<{
  isLogoShowing: boolean;
}>`
  display: grid;
  grid-template-rows: min-content min-content;
  grid-row-gap: ${(props) => (props.isLogoShowing ? rem(32) : 0)};
  grid-template-areas:
    "logo"
    "heading";

  ${(props) => props.$viewport.tablet`
    display: contents;
  `}
`;

export const RightColumn = withViewportStyle("div")`
  display: grid;
  grid-template-rows: min-content min-content;
  grid-row-gap: ${rem(24)};
  grid-template-areas:
    "ctaText"
    "subheading"
    "button";

  ${(props) => props.$viewport.tablet`
    display: contents;
  `}
`;

export const LogoWrapper = withViewportStyle("div")`
  height: 100%;
  display: flex;
  align-items: center;

  ${(props) => props.$viewport.tablet`
    justify-content: center;
  `}
`;

export const Logo = withViewportStyle("img")<{ isLogoResized: boolean }>`
  max-height: ${({ isLogoResized }) => (isLogoResized ? rem(120) : rem(40))};
  max-width: 100%;
  object-fit: contain;

  ${(props) => props.$viewport.tablet`
    max-height: ${({ isLogoResized }) => (isLogoResized ? rem(100) : rem(40))};
  `}

  ${(props) => props.$viewport.mobile`
    max-height: ${({ isLogoResized }) => (isLogoResized ? rem(80) : rem(40))};
  `}
`;

export const HeadingWrapper = withViewportStyle("div")`
  grid-area: heading;

  ${(props) => props.$viewport.tablet`
    margin-bottom: 0;
  `}
`;

export const CTATextWrapper = withViewportStyle("div")`
  grid-area: ctaText;

  .text-primitive {
    display: inline-flex;
    padding: ${rem(24)} 0;

    ${(props) => props.$viewport.tablet`
      display: block;
      padding: 0;
    `}
  }
`;

export const SubheadingWrapper = styled.div`
  grid-area: subheading;
`;

export const ButtonWrapper = withViewportStyle(StyledButtonContainer)`
  grid-area: button;

  ${(props) => props.$viewport.tablet`
    text-align: center;
    margin-bottom: ${rem(37)};
  `}

  ${(props) => props.$viewport.mobile`
    margin-bottom: ${rem(24)};
  `}
`;

export const ImageWrapper = withViewportStyle("div")`
  height: ${rem(400)};

  ${(props) => props.$viewport.tablet`
    height: ${rem(334)};
  `}

  ${(props) => props.$viewport.mobile`
    height: ${rem(268)};
  `}
`;

export const Image = styled.img<{
  $position: ImagePosition;
  imageFit?: ImageFit;
}>`
  width: 100%;
  height: 100%;
  object-fit: ${({ imageFit }) => imageFit || "cover"};
  object-position: ${(props) => props.$position};
  overflow: hidden;
`;
